import type { DataSheetColumn } from '@components/ui/DataSheet';
import { Progress } from '@mantine/core';
import { formatCount } from '@utils/currency';

import { BundleKindPill, HeaderHint } from './BundleCells';
import { BUNDLE_KIND_LABEL } from './bundleReporting';
import styles from './bundleColumns.module.css';
import type { AsSoldRow, BundleKind, SkuRow } from './types';

/* Column builders for Sales page bundle reporting (cells live in BundleCells). Kept out of index.tsx, which is already
 * long; the page passes in its own money formatter so both views read the same. */

const ALLOCATED_REVENUE_HINT =
    "A bundle's revenue is split across the products inside it by each product's typical individual price.";

/** Header for the revenue column while bundle reporting is on. */
export const allocatedRevenueHeader = () => (
    <HeaderHint label="Allocated revenue" hint={ALLOCATED_REVENUE_HINT} />
);

/** The two By product columns, inserted after Units. */
export const buildBundleShareColumns = (): DataSheetColumn<SkuRow>[] => [
    {
        key: 'soldIndividuallyPct',
        header: 'Sold individually',
        width: 150,
        sortable: true,
        render: (value: number | undefined, row: SkuRow) => {
            if (row.isBundleChild || value == null) return null;
            const pct = Math.round(value);
            return (
                <div className={styles.share}>
                    <span className={styles.sharePct}>{pct}%</span>
                    <Progress value={pct} size="sm" color="teal" aria-hidden="true" />
                </div>
            );
        },
    },
    {
        key: 'bundleCount',
        header: 'Sold in',
        width: 110,
        render: (value: number | undefined, row: SkuRow) => {
            if (row.isBundleChild) {
                return row.bundleKind ? <BundleKindPill kind={row.bundleKind} /> : null;
            }
            if (!value) return null;
            return (
                <span className={styles.muted}>
                    {value} {value === 1 ? 'bundle' : 'bundles'}
                </span>
            );
        },
    },
];

export const buildAsSoldColumns = (
    rankOffset: number,
    formatMoney: (n: number) => string,
): DataSheetColumn<AsSoldRow>[] => [
    {
        key: 'rank',
        header: '#',
        width: 50,
        align: 'center',
        render: (_v: unknown, _row: AsSoldRow, idx: number) => (
            <span className={styles.muted}>{rankOffset + idx + 1}</span>
        ),
    },
    {
        key: 'name',
        header: 'Sold as',
        width: 280,
        sticky: true,
        render: (_v: unknown, row: AsSoldRow) => (
            <div className={styles.identity}>
                <span className={styles.identityName}>{row.name || '—'}</span>
                <span className={styles.identityCode}>{row.sku || row.id}</span>
            </div>
        ),
    },
    {
        key: 'kind',
        header: 'Type',
        width: 110,
        render: (value: BundleKind) => <BundleKindPill kind={value} />,
        csvValue: (_v, row) => BUNDLE_KIND_LABEL[row.kind],
    },
    {
        key: 'unitsSold',
        header: 'Units sold',
        renderHeader: () => <HeaderHint label="Units sold" hint="A bundle or multipack counts once." />,
        width: 110,
        align: 'right',
        sortable: true,
        render: (value: number) => <span className={styles.strongNum}>{formatCount(value) ?? '—'}</span>,
    },
    {
        key: 'revenue',
        header: 'Revenue',
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number) => <span className={styles.strongNum}>{formatMoney(value)}</span>,
    },
    {
        key: 'contains',
        header: 'Contains',
        width: 320,
        render: (value: string) => <span className={styles.muted}>{value || '—'}</span>,
    },
];
